import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { bySiteId } from "~/hec/utils/site-id";

import { SHOWS_CARING_MILL_COMPARE_LINK } from "./pdp-shared-constants";

export interface CompareToData {
  name: string;
  url: string;
  formattedPrice: string;
}

/**
 * "Compare to" message — populated by loader when product has `c_compareToSKU`.
 * FSA renders the Caring Mill-styled bg + leaf-mark link; HSA/WDH render a plain
 * inline message. Two layout variants:
 *   - default: padded; spans the column on mobile, hugs the copy from `sm` up —
 *     for the main PDP info column
 *   - compact: tight, hugs the copy — for HybridPDP buy column
 *
 * Originally inlined in `product-details.tsx`, `WDH-PDP/wdh-pdp.tsx`, and
 * `Hybrid-PDP/hybrid-pdp.tsx`. Extracted to remove triplicated JSX.
 */
export function CompareToMessage({
  data,
  siteId,
  compact = false,
}: {
  data: CompareToData | undefined;
  siteId: string;
  compact?: boolean;
}) {
  if (!data) return null;

  if (bySiteId(siteId, SHOWS_CARING_MILL_COMPARE_LINK)) {
    const link = (
      <Link
        to={data.url}
        prefetch="intent"
        data-testid="pdp-compare-to-link"
        className={
          compact
            ? "mb-3 mt-2 block w-fit rounded-lg bg-[#e1ebfe] px-2.5 pb-1.5 pt-[9px] text-sm leading-[normal] hover:no-underline md:px-2 md:pb-[3px] md:pt-1.5"
            : "mb-3 block w-full rounded-lg bg-[#e1ebfe] px-2 pb-1.5 pt-[9px] text-sm leading-[normal] hover:no-underline"
        }
      >
        <img
          src="https://assets-manager.abtasty.com/90f64213b29a76219569f9b9a8d26cf1/account/caringmill-mark.svg"
          alt=""
          aria-hidden="true"
          className="mb-1 inline-block h-4 w-4"
        />{" "}
        Compare to {data.name}{" "}
        <span className="font-extrabold">{data.formattedPrice}</span>
        {/* COM-86 / PI-189: affordance chevron, inline after the price rather
            than pinned to a far edge — the box hugs its copy, so there is no
            "far edge" to pin to (Figma on COM-86, 2026-08-25). Decorative:
            the link's own text is the accessible name, exactly like the leaf
            mark above. `currentColor` rather than a hardcoded navy so it
            tracks the link colour on every site instead of drifting from it. */}
        <ChevronRight
          aria-hidden="true"
          focusable="false"
          className="ml-1 inline-block h-4 w-4 shrink-0 align-text-bottom"
          data-testid="pdp-compare-to-chevron"
        />
      </Link>
    );

    // Non-compact (main PDP info column) is rendered as a bare sibling in the
    // details rail, whose flex column is `items-center`. Alignment and width are
    // separate concerns here and must stay that way (COM-86):
    //   - `self-start` owns alignment — it opts this child out of the column's
    //     centering, so the box is left-aligned at any width. Do NOT go back to
    //     relying on `w-full` for this: stretching to 100% only hides the
    //     centering, and it returns the moment the width is constrained.
    //   - width is a UX decision, not a layout fallout: desktop hugs the copy
    //     (`sm:w-auto`) because the full-column blue box left dead space to the
    //     right of the text; mobile keeps `w-full` so the box spans the column
    //     inside its `px-5` gutter, where the copy wraps and fills it anyway.
    if (compact) return link;
    return (
      <div className="mt-2 w-full self-start px-5 sm:w-auto sm:px-0">
        {link}
      </div>
    );
  }

  return (
    <div
      className={
        compact
          ? "mt-2 text-sm text-foreground"
          : "mt-2 w-full px-5 text-sm text-foreground sm:px-0"
      }
    >
      Compare to{" "}
      <Link
        to={data.url}
        className="underline"
        prefetch="intent"
        data-testid="pdp-compare-to-link"
      >
        {data.name}
      </Link>{" "}
      at {data.formattedPrice}
    </div>
  );
}
